import {
  EndpointMethod,
  IntegrationEndpointBaseVariable,
} from '@components/integrations/integrations-types';
import { JsonValue } from '@components/json-editor/json-editor';
import { FieldType } from '@components/text-editors/addons/field-mapping';

import { request } from 'api/request';
import { Condition, ConditionType, MappableVariable } from 'api/types';

type VariablesType = {
  name: string;
  type: FieldType;
  label: string;
};

type PollingIntegrationEndpointBaseVariable = IntegrationEndpointBaseVariable & {
  value: string;
};

export type CreatePollingConfigType = {
  journey: string;
  integration: string;
  method: EndpointMethod;
  variables?: PollingIntegrationEndpointBaseVariable[];
  description?: string;
  endpoint?: string;
  url: string;
  isCustomUrl?: boolean;
};

export type PollingConfigList = {
  shortId: string;
  journeyShortId: string;
  description: string;
  isActive: boolean;
  isAutoStart: boolean;
};

export type PollingConfig = PollingConfigList & {
  payloadVariables: VariablesType[];
  pollingInterval:
    | 'six_hours'
    | 'twelve_hours'
    | 'every_day'
    | 'every_seven_days'
    | 'every_fourteen_days'
    | 'first_of_month'
    | 'last_of_month'
    | 'once_every_month';
  dayOfMonth: number;
  startDatetime: Date;
  endDatetime: Date | null;
  conditions: {
    conditions: Condition[];
    conditionType: ConditionType;
  };
  mappings: { [key: string]: { value: string; variables: Record<string, MappableVariable> } };
  variables: PollingIntegrationEndpointBaseVariable[];
  requestData: { [key: string]: string };
  integrationName: string;
  endpointName: string;
  logoThumbnail: string | null;
  url: string;
  logsCount: number;
  isCustomUrl: boolean;
  timezone: string;
};

export const createJourneyPollingConfig = async (data: CreatePollingConfigType) => {
  const response = await request.post<PollingConfig>('v1/journey-polling/', data);
  return response.data;
};

export const updateJourneyPollingConfig = async (
  pollingConfigId: string,
  data: Partial<PollingConfig>
) => {
  const response = await request.patch<PollingConfig>(`v1/journey-polling/${pollingConfigId}/`, {
    ...data,
  });
  return response.data;
};

export const journeyPollingConfig = async (data: CreatePollingConfigType) => {
  const response = await request.post<PollingConfig>('v1/journey-polling/', data);
  return response.data;
};

export const loadJourneyPollingConfigs = async (journeyId: string) => {
  const response = await request.get<PollingConfigList[]>(
    `v1/journey-polling/?journey_id=${journeyId}`
  );
  return response.data;
};

export const loadJourneyPollingConfig = async (pollingConfigShortId: string) => {
  const response = await request.get<PollingConfig>(`v1/journey-polling/${pollingConfigShortId}/`);
  return response.data;
};

export const deleteJourneyPollingConfig = async (pollingConfigId: string) => {
  const response = await request.delete<void>(`v1/journey-polling/${pollingConfigId}/`);
  return response.data;
};

export const pollJourneyPollingSchedule = async (pollingConfigId: string) => {
  const response = await request.post<{ numTravellerJourneysCreated: number }>(
    `v1/journey-polling/${pollingConfigId}/`
  );
  return response.data;
};

export type PollingTestRequestData = {
  integrationShortId: string;
  url?: string;
  endpointShortId?: string;
  method?: EndpointMethod;
};

export type PollingTestRequestType = {
  success: boolean;
  errorMessage: string;
};

export const pollingTestRequest = async (data: PollingTestRequestData) => {
  const response = await request.post<PollingTestRequestType>(
    'v1/journey-polling/test-custom-url/',
    data
  );
  return response.data;
};

export type JourneyPollingLogs = {
  shortId: string;
  triggeredOnDate: Date;
  triggeredOnTime: Date;
  numTravellerJourneysCreated: number;
};

export const loadJourneyPollingLogs = async (pollingConfigShortId: string) => {
  const response = await request.get<JourneyPollingLogs[]>(
    `v1/journey-polling/${pollingConfigShortId}/logs`
  );
  return response.data;
};

export type JourneyPollingLog = Omit<JourneyPollingLogs, 'triggeredOnTime'> & {
  payload: JsonValue;
  errorMessage: string;
};

export const loadJourneyPollingLog = async (
  pollingConfigShortId: string,
  pollingLogShortId: string
) => {
  const response = await request.get<JourneyPollingLog>(
    `v1/journey-polling/${pollingConfigShortId}/logs/${pollingLogShortId}`
  );
  return response.data;
};
